import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FilterFieldDef } from "@/lib/useFieldFilters";

type Kind = FilterFieldDef["type"];

/**
 * What the two directions are called for a kind of field: A → Z for text and choices, Descending / Ascending for numbers and
 * dates (the left one is descending, as in HubSpot). `first`/`second` are the sort values, so the wording follows the field.
 */
const directionLabels = (type: Kind | undefined): { desc: string; asc: string } =>
  type === "text" || type === "enum" ? { desc: "Z → A", asc: "A → Z" } : { desc: "Descending", asc: "Ascending" };

/**
 * "Sort by": a box with the field to sort by (a searchable list grouped like the filter fields, starting with the page's own
 * order) and the direction, worded for the kind of field. `value` is "" (own order), "field" or "-field". Applies as you choose.
 */
export function SortMenu({ fields, value, onChange, defaultLabel = "Default order" }: { fields: FilterFieldDef[]; value: string; onChange: (v: string) => void; defaultLabel?: string }) {
  const [open, setOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const sortable = useMemo(() => fields.filter((f) => ["text", "number", "date", "enum"].includes(f.type)), [fields]);
  const desc = value.startsWith("-");
  const key = value.replace(/^-/, "");
  const current = sortable.find((f) => f.key === key);
  const labels = directionLabels(current?.type);

  const close = () => {
    setOpen(false);
    setListOpen(false);
    setQ("");
  };
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && close();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (listOpen) setListOpen(false);
      else close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, listOpen]);

  if (!sortable.length) return null;
  const shown = sortable.filter((f) => !q.trim() || f.label.toLowerCase().includes(q.trim().toLowerCase()));
  const groups = shown.reduce<Record<string, FilterFieldDef[]>>((acc, f) => ((acc[f.group] ||= []).push(f), acc), {});
  const pick = (f: FilterFieldDef | null) => {
    // Keep the direction the person had chosen; a new field starts ascending
    onChange(f ? (desc && current ? `-${f.key}` : f.key) : "");
    setListOpen(false);
    setQ("");
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        className={cn(
          "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border bg-background px-3 text-xs font-medium transition-colors hover:bg-muted",
          current ? "border-primary/40 bg-accent text-accent-foreground" : "border-border",
          open && "ring-2 ring-primary/30",
        )}
      >
        {current ? desc ? <ArrowDown className="size-3.5" /> : <ArrowUp className="size-3.5" /> : <ArrowUpDown className="size-3.5" />}
        {current ? `Sort: ${current.label}` : "Sort by"}
      </button>

      {open && (
        <div className="z-50 w-[440px] max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-popover p-3.5 text-popover-foreground shadow-xl animate-in fade-in-50 zoom-in-95 duration-100 max-sm:fixed max-sm:inset-x-4 max-sm:bottom-4 max-sm:w-auto sm:absolute sm:left-0 sm:top-full sm:mt-1.5">
          <h3 className="mb-3 text-sm font-semibold">Sort by</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setListOpen((o) => !o)}
              aria-expanded={listOpen}
              aria-haspopup="listbox"
              className={cn("flex h-8 min-w-0 flex-1 cursor-pointer items-center justify-between gap-2 rounded-md border border-border bg-background px-2.5 text-left text-[13px] hover:bg-muted", listOpen && "ring-2 ring-primary/30")}
            >
              <span className="truncate">{current ? current.label : defaultLabel}</span>
              <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
            </button>
            <div role="group" aria-label="Direction" className={cn("flex shrink-0 overflow-hidden rounded-md border border-border text-xs", !current && "pointer-events-none opacity-45")}>
              {[true, false].map((isDesc) => (
                <button
                  key={String(isDesc)}
                  type="button"
                  aria-pressed={!!current && desc === isDesc}
                  onClick={() => current && onChange(isDesc ? `-${key}` : key)}
                  className={cn("h-8 cursor-pointer whitespace-nowrap px-2.5 transition-colors first:border-r first:border-border", current && desc === isDesc ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted")}
                >
                  {isDesc ? labels.desc : labels.asc}
                </button>
              ))}
            </div>
          </div>

          {listOpen && (
            <div role="listbox" className="mt-2 overflow-hidden rounded-lg border border-border bg-background">
              <div className="relative border-b border-border p-2">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="h-8 w-full rounded-md border border-border bg-background pl-7 pr-2 text-xs outline-none focus:ring-1 focus:ring-primary" />
              </div>
              <div className="max-h-[min(220px,calc(100vh-420px))] min-h-[120px] overflow-y-auto py-1">
                {!q.trim() && (
                  <button type="button" role="option" aria-selected={!current} onClick={() => pick(null)} className={cn("flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted", !current && "bg-muted/60 font-semibold")}>
                    {defaultLabel}
                    {!current && <Check className="size-3.5 text-primary" />}
                  </button>
                )}
                {Object.entries(groups).map(([group, items]) => (
                  <div key={group}>
                    <div className="px-3 pb-1 pt-2.5 text-xs font-semibold">{group}</div>
                    {items.map((f) => (
                      <button key={f.key} type="button" role="option" aria-selected={f.key === key} onClick={() => pick(f)} className={cn("flex w-full cursor-pointer items-center justify-between py-1.5 pl-6 pr-3 text-left text-sm hover:bg-muted", f.key === key && "bg-muted/60 font-semibold")}>
                        <span className="truncate">{f.label}</span>
                        {f.key === key && <Check className="size-3.5 shrink-0 text-primary" />}
                      </button>
                    ))}
                  </div>
                ))}
                {!shown.length && <div className="px-3 py-4 text-center text-xs text-muted-foreground">No field matches</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
